import { useEffect, useState, useCallback, useRef } from "react";
import { trainingApi } from "@/api";
import {
  BookOpen, AlertTriangle, CheckCircle, Clock, RefreshCw,
  Shield, Users, TrendingUp, Filter, ChevronDown, Download, FileText, FileSpreadsheet, X,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { exportPDF, exportCSV, todayStr } from "@/utils/exportUtils";

const STATUS_CFG = {
  valid:             { label: "Valid",           cls: "text-green-400  bg-green-400/10  border-green-400/30"  },
  expiring_soon:     { label: "Expiring 90d",    cls: "text-amber-400  bg-amber-400/10  border-amber-400/30"  },
  expiring_critical: { label: "Expiring 30d",    cls: "text-orange-400 bg-orange-400/10 border-orange-400/30" },
  overdue:           { label: "OVERDUE",         cls: "text-red-400    bg-red-400/10    border-red-400/30"    },
};

function KpiCard({ label, value, color, Icon, sub }) {
  return (
    <div className="bg-card border border-border rounded-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</span>
        <Icon size={14} className={color} />
      </div>
      <div className={`text-3xl font-mono font-black ${color}`}>{value ?? "—"}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

// ─── Tab: Overview ────────────────────────────────────────────────────────────
function OverviewTab({ overview }) {
  if (!overview) return null;
  const { modules = [] } = overview;

  const chartData = modules.slice(0, 10).map(m => ({
    name:    m.code.replace("_AWARENESS","").replace("_ACAS",""),
    valid:   m.valid,
    expiring:m.expiring,
    overdue: m.overdue,
    critical:m.critical,
  }));

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Total Records"   value={overview.total_records}  color="text-primary"   Icon={BookOpen}/>
        <KpiCard label="Overdue"         value={overview.overdue}        color="text-red-400"   Icon={AlertTriangle} sub="immediate action"/>
        <KpiCard label="Expiring ≤30d"   value={overview.expiring_30d}   color="text-orange-400"Icon={Clock}/>
        <KpiCard label="Compliance"      value={`${overview.compliance_pct}%`} color="text-green-400" Icon={Shield} sub={`${overview.crew_count} crew tracked`}/>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <div className="text-xs font-black uppercase tracking-widest text-muted-foreground">Compliance by Module</div>
          <div className="text-xs text-muted-foreground mt-0.5">DGCA-required training modules — valid vs expiring vs overdue</div>
        </div>
        <div className="p-4">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} />
              <YAxis tick={{ fill: "#94a3b8", fontSize: 10 }} />
              <Tooltip
                contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", fontSize: 11 }}
                labelStyle={{ color: "#f8fafc" }}
              />
              <Bar dataKey="valid"    fill="#22c55e" stackId="a" name="Valid"    radius={[0,0,0,0]} />
              <Bar dataKey="expiring" fill="#f59e0b" stackId="a" name="Expiring" radius={[0,0,0,0]} />
              <Bar dataKey="overdue"  fill="#ef4444" stackId="a" name="Overdue"  radius={[2,2,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-border text-xs font-black uppercase tracking-widest text-muted-foreground">
          Module Compliance Detail
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border bg-background/40">
                {["Module","Regulatory Ref","Critical","Valid","Expiring","Overdue","Rate"].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {modules.map(m => {
                const total     = m.total || 1;
                const validPct  = Math.round((m.valid / total) * 100);
                const barColor  = m.overdue > 0 ? "#ef4444" : m.expiring > 0 ? "#f59e0b" : "#22c55e";
                return (
                  <tr key={m.code} className="border-b border-border/40 hover:bg-secondary/20">
                    <td className="px-3 py-2.5 font-medium text-foreground">{m.label}</td>
                    <td className="px-3 py-2.5 font-mono text-muted-foreground text-[11px]">{m.code}</td>
                    <td className="px-3 py-2.5">
                      {m.critical && <span className="text-[10px] font-bold text-red-400 border border-red-400/30 px-1.5 py-0.5 rounded-sm">CRITICAL</span>}
                    </td>
                    <td className="px-3 py-2.5 text-green-400 font-mono font-bold">{m.valid}</td>
                    <td className="px-3 py-2.5 text-amber-400 font-mono font-bold">{m.expiring}</td>
                    <td className="px-3 py-2.5 text-red-400 font-mono font-bold">{m.overdue}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-secondary rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${validPct}%`, background: barColor }} />
                        </div>
                        <span className="font-mono text-xs font-bold" style={{ color: barColor }}>{validPct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Training Scheduling Wizard ──────────────────────────────────────────────
function TrainingWizardModal({ preselected = [], onClose, onScheduled }) {
  const [step, setStep] = useState(1);
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedCrew, setSelectedCrew] = useState(preselected);
  const [slots, setSlots] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [instructor, setInstructor] = useState("TBD");
  const [notes, setNotes] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    trainingApi.getWizardModules().then(r => setModules(r.data || []));
  }, []);

  // Auto-select module if all preselected crew share the same module
  useEffect(() => {
    if (preselected.length > 0 && preselected[0].module_code) {
      const code = preselected[0].module_code;
      const allSame = preselected.every(c => c.module_code === code);
      if (allSame && modules.length > 0) {
        const mod = modules.find(m => m.module_code === code);
        if (mod) setSelectedModule(mod);
      }
    }
  }, [preselected, modules]);

  const fetchSlots = async () => {
    if (!selectedModule || selectedCrew.length === 0) return;
    setLoadingSlots(true);
    const base = selectedCrew[0]?.base || "DEL";
    const ids   = selectedCrew.map(c => c.crew_id).join(",");
    try {
      const r = await trainingApi.suggestSlots({ module_code: selectedModule.module_code, base, crew_ids: ids });
      setSlots(r.data);
    } catch (e) { console.error(e); }
    finally { setLoadingSlots(false); }
  };

  const handleConfirm = async () => {
    if (!selectedSlot || !selectedModule) return;
    setScheduling(true);
    try {
      const r = await trainingApi.scheduleBlock({
        crew_ids:       selectedCrew.map(c => c.crew_id),
        module_code:    selectedModule.module_code,
        scheduled_date: selectedSlot.date,
        time_slot:      selectedSlot.time,
        instructor,
        notes,
        venue:          selectedSlot.venue,
      });
      setResult(r.data);
      setStep(4);
      // NOTE: onScheduled is called only when user clicks Done (step 4)
    } catch (e) { console.error(e); }
    finally { setScheduling(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="training-wizard-modal">
      <div className="bg-card border border-border rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Training Scheduling Wizard</div>
            <div className="text-sm font-black text-foreground mt-0.5">
              {step === 1 ? "Step 1 — Select Module" : step === 2 ? "Step 2 — Confirm Crew" : step === 3 ? "Step 3 — Pick Date & Confirm" : "Scheduled"}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-1">
              {[1,2,3,4].map(s => (
                <div key={s} className={`w-5 h-1 rounded-full transition-colors ${s <= step ? "bg-primary" : "bg-border"}`} />
              ))}
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
          </div>
        </div>

        <div className="p-5">
          {/* ── Step 1: Module Selection ── */}
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Select the training module to schedule. Only modules with overdue or expiring crew are shown.</p>
              <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                {modules.length === 0 ? (
                  <div className="text-xs text-muted-foreground py-4 text-center">Loading modules...</div>
                ) : modules.map(m => (
                  <button key={m.module_code} onClick={() => setSelectedModule(m)}
                    className={`w-full flex items-center justify-between p-3 border rounded-sm text-left transition-all ${
                      selectedModule?.module_code === m.module_code
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/40 bg-background/40"
                    }`}
                    data-testid={`wizard-module-${m.module_code}`}>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-foreground">{m.module_name}</span>
                        {m.critical && <span className="text-[10px] font-bold text-red-400 border border-red-400/30 px-1 rounded-sm">CRITICAL</span>}
                      </div>
                      <div className="text-[11px] text-muted-foreground font-mono mt-0.5">{m.module_code} · {m.regulatory_ref}</div>
                    </div>
                    <div className="flex gap-2 text-xs shrink-0">
                      {m.overdue > 0 && <span className="px-1.5 py-0.5 bg-red-500/15 text-red-400 rounded font-mono font-bold">{m.overdue} OD</span>}
                      {m.expiring_30d > 0 && <span className="px-1.5 py-0.5 bg-orange-500/15 text-orange-400 rounded font-mono font-bold">{m.expiring_30d} &lt;30d</span>}
                      {m.expiring_90d > 0 && <span className="px-1.5 py-0.5 bg-amber-500/15 text-amber-400 rounded font-mono font-bold">{m.expiring_90d} &lt;90d</span>}
                    </div>
                  </button>
                ))}
              </div>
              <div className="flex justify-end pt-2 border-t border-border">
                <button disabled={!selectedModule} onClick={() => setStep(2)}
                  className="px-4 py-2 bg-primary text-primary-foreground text-xs font-bold rounded-sm hover:bg-primary/90 disabled:opacity-40 transition-colors"
                  data-testid="wizard-step1-next">
                  Next — Review Crew →
                </button>
              </div>
            </div>
          )}

          {/* ── Step 2: Crew Review ── */}
          {step === 2 && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Module: <span className="text-foreground font-semibold">{selectedModule?.module_name}</span> · {selectedCrew.length} crew selected
              </p>
              <div className="bg-background/50 border border-border rounded-sm overflow-hidden max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-border bg-background/40">
                    {["Emp ID","Name","Rank","Base","Status"].map(h => (
                      <th key={h} className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {selectedCrew.map(c => (
                      <tr key={`${c.crew_id}-${c.module_code}`} className="border-b border-border/40 hover:bg-secondary/20">
                        <td className="px-3 py-2 font-mono text-blue-400 text-[11px]">{c.employee_id}</td>
                        <td className="px-3 py-2 font-medium text-foreground">{c.crew_name}</td>
                        <td className="px-3 py-2 text-muted-foreground">{c.rank}</td>
                        <td className="px-3 py-2 font-mono text-muted-foreground">{c.base}</td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] font-bold border px-1.5 py-0.5 rounded-sm ${
                            (STATUS_CFG[c.status] || STATUS_CFG.valid).cls
                          }`}>{(STATUS_CFG[c.status] || STATUS_CFG.valid).label}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-between pt-2 border-t border-border">
                <button onClick={() => setStep(1)} className="px-3 py-2 border border-border text-xs text-muted-foreground rounded-sm hover:text-foreground transition-colors">← Back</button>
                <button onClick={() => { setStep(3); fetchSlots(); }}
                  disabled={selectedCrew.length === 0}
                  className="px-4 py-2 bg-primary text-primary-foreground text-xs font-bold rounded-sm hover:bg-primary/90 disabled:opacity-40 transition-colors"
                  data-testid="wizard-step2-next">
                  Next — Suggest Slots →
                </button>
              </div>
            </div>
          )}

          {/* ── Step 3: Slot Selection + Confirm ── */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground">
                Suggested dates when selected crew are not on flight duties. New expiry will be <span className="font-mono text-foreground">{selectedSlot?.new_expiry || "—"}</span>
              </p>
              {loadingSlots ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground py-8 justify-center">
                  <RefreshCw size={13} className="animate-spin" /> Finding available slots...
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto">
                  {(slots?.slots || []).map((slot, i) => (
                    <button key={i} onClick={() => { setSelectedSlot(slot); if (slot.suggested_instructor) setInstructor(slot.suggested_instructor); }}
                      className={`p-3 border rounded-sm text-left transition-all ${
                        selectedSlot?.date === slot.date
                          ? "border-primary bg-primary/10"
                          : slot.is_full_availability
                          ? "border-green-500/30 bg-green-500/5 hover:border-green-500/60"
                          : "border-border hover:border-primary/40 bg-background/40"
                      }`}
                      data-testid={`wizard-slot-${i}`}>
                      <div className="text-xs font-bold text-foreground">{slot.label}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">{slot.time} · {slot.venue}</div>
                      {slot.suggested_instructor && (
                        <div className="text-[11px] text-blue-400 mt-0.5">{slot.suggested_instructor}</div>
                      )}
                      <div className={`text-[11px] font-bold mt-1 ${slot.is_full_availability ? "text-green-400" : "text-amber-400"}`}>
                        {slot.available_crew}/{slot.total_crew} crew free
                      </div>
                    </button>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1 block">Instructor</label>
                  <input value={instructor} onChange={e => setInstructor(e.target.value)}
                    placeholder="Instr. name or TBD"
                    className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1 block">Notes</label>
                  <input value={notes} onChange={e => setNotes(e.target.value)}
                    placeholder="Optional notes..."
                    className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                </div>
              </div>
              <div className="flex justify-between pt-2 border-t border-border">
                <button onClick={() => setStep(2)} className="px-3 py-2 border border-border text-xs text-muted-foreground rounded-sm hover:text-foreground transition-colors">← Back</button>
                <button disabled={!selectedSlot || scheduling} onClick={handleConfirm}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-xs font-bold rounded-sm hover:bg-primary/90 disabled:opacity-40 transition-colors"
                  data-testid="wizard-confirm-btn">
                  {scheduling ? <><RefreshCw size={11} className="animate-spin" /> Scheduling...</> : "Confirm & Schedule"}
                </button>
              </div>
            </div>
          )}

          {/* ── Step 4: Success ── */}
          {step === 4 && result && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-sm">
                <CheckCircle size={20} className="text-green-400 shrink-0" />
                <div>
                  <div className="text-sm font-bold text-green-400">Training Block Scheduled</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Block ID: <span className="font-mono text-foreground">{result.block_id}</span></div>
                </div>
              </div>
              <div className="bg-background/50 border border-border rounded-sm p-4 space-y-2 text-xs">
                {[
                  ["Module",   result.module_name],
                  ["Date",     `${result.scheduled_date} at ${result.time_slot}`],
                  ["Venue",    result.venue],
                  ["Instructor", result.instructor || "TBD"],
                  ["Crew scheduled", `${result.updated_count} members`],
                  ["New expiry",  result.new_expiry],
                ].map(([label, val]) => (
                  <div key={label} className="flex justify-between gap-4">
                    <span className="text-muted-foreground font-medium">{label}</span>
                    <span className="font-mono text-foreground">{val}</span>
                  </div>
                ))}
              </div>
              <div className="bg-card border border-border rounded-sm overflow-hidden">
                <div className="px-3 py-2 border-b border-border text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Crew Updated</div>
                {result.crew.map(c => (
                  <div key={c.crew_id} className="flex items-center justify-between px-3 py-2 border-b border-border/40 text-xs">
                    <div className="font-medium text-foreground">{c.crew_name}</div>
                    <div className="text-muted-foreground">{c.rank} · {c.base}</div>
                    <div className="text-green-400 font-mono text-[11px]">New expiry: {c.new_expiry?.substring(0,10)}</div>
                  </div>
                ))}
              </div>
              <div className="flex justify-end pt-2 border-t border-border">
                <button onClick={() => { onClose(); onScheduled?.(); }}
                  className="px-4 py-2 bg-primary text-primary-foreground text-xs font-bold rounded-sm hover:bg-primary/90 transition-colors"
                  data-testid="wizard-close-success">
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Expiry Watch ────────────────────────────────────────────────────────
function ExpiryWatchTab({ records, onScheduleSelected }) {
  const [filter, setFilter] = useState("all");
  const [checked, setChecked] = useState(new Set());
  const filtered = filter === "all" ? records : records.filter(r => r.status === filter);

  const toggleRow = (key) => setChecked(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  const toggleAll = () => {
    if (checked.size === filtered.length) { setChecked(new Set()); }
    else { setChecked(new Set(filtered.map(r => `${r.crew_id}-${r.module_code}`))); }
  };

  const selectedRecords = filtered.filter(r => checked.has(`${r.crew_id}-${r.module_code}`));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Filter size={12} className="text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Filter:</span>
        {[
          { id: "all",              label: "All" },
          { id: "overdue",          label: "Overdue" },
          { id: "expiring_critical",label: "≤30 days" },
          { id: "expiring_soon",    label: "≤90 days" },
        ].map(opt => (
          <button key={opt.id} onClick={() => setFilter(opt.id)}
            className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${
              filter === opt.id
                ? "bg-primary text-primary-foreground border-primary"
                : "border-border text-muted-foreground hover:text-foreground"
            }`}>
            {opt.label}
          </button>
        ))}
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length} records</span>
        {selectedRecords.length > 0 && (
          <button
            onClick={() => onScheduleSelected(selectedRecords)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-bold rounded-sm hover:bg-primary/90 transition-colors"
            data-testid="schedule-selected-btn">
            <Clock size={11} /> Schedule {selectedRecords.length} Selected
          </button>
        )}
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="training-expiry-table">
            <thead>
              <tr className="border-b border-border bg-background/40">
                <th className="px-3 py-2.5 w-8">
                  <input type="checkbox"
                    checked={checked.size === filtered.length && filtered.length > 0}
                    onChange={toggleAll}
                    className="accent-primary cursor-pointer"
                    data-testid="training-select-all" />
                </th>
                {["Employee","Name","Rank","Base","Module","Expiry","Days Left","Status","Reg. Ref"].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 150).map((r, i) => {
                const key = `${r.crew_id}-${r.module_code}`;
                const isChecked = checked.has(key);
                const st = STATUS_CFG[r.status] || STATUS_CFG.valid;
                const isOverdue = r.days_remaining < 0;
                return (
                  <tr key={key} onClick={() => toggleRow(key)}
                    className={`border-b border-border/40 cursor-pointer transition-colors ${
                      isChecked ? "bg-primary/8 hover:bg-primary/12" :
                      isOverdue ? "bg-red-500/5 hover:bg-red-500/10" :
                      i % 2 !== 0 ? "bg-secondary/10 hover:bg-secondary/20" : "hover:bg-secondary/10"
                    }`}
                    data-testid={`training-row-${key}`}>
                    <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={isChecked} onChange={() => toggleRow(key)}
                        className="accent-primary cursor-pointer" />
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-blue-400">{r.employee_id}</td>
                    <td className="px-3 py-2 font-medium text-foreground whitespace-nowrap">{r.crew_name}</td>
                    <td className="px-3 py-2 text-muted-foreground">{r.rank}</td>
                    <td className="px-3 py-2 font-mono text-muted-foreground">{r.base}</td>
                    <td className="px-3 py-2 font-medium text-foreground">{r.module_name}</td>
                    <td className="px-3 py-2 font-mono text-muted-foreground whitespace-nowrap">{r.expiry_date?.substring(0,10)}</td>
                    <td className={`px-3 py-2 font-mono font-bold whitespace-nowrap ${isOverdue ? "text-red-400" : r.days_remaining <= 30 ? "text-orange-400" : r.days_remaining <= 90 ? "text-amber-400" : "text-green-400"}`}>
                      {isOverdue ? `${Math.abs(r.days_remaining)}d overdue` : `${r.days_remaining}d`}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-bold border px-1.5 py-0.5 rounded-sm whitespace-nowrap ${st.cls}`}>{st.label}</span>
                      {r.critical && <span className="ml-1 text-[10px] font-bold text-red-400">★</span>}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground text-[11px] font-mono">{r.regulatory_ref}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-muted-foreground">No records match filter</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Compliance Matrix ───────────────────────────────────────────────────
function ComplianceMatrixTab({ matrix }) {
  if (!matrix) return null;
  const { by_base = [], by_rank = [] } = matrix;

  function ComplianceBar({ valid, expiring, overdue }) {
    const total = valid + expiring + overdue || 1;
    return (
      <div className="flex h-2 rounded-sm overflow-hidden w-32">
        <div className="bg-green-500" style={{ width: `${(valid    / total) * 100}%` }} />
        <div className="bg-amber-500" style={{ width: `${(expiring / total) * 100}%` }} />
        <div className="bg-red-500"   style={{ width: `${(overdue  / total) * 100}%` }} />
      </div>
    );
  }

  function SectionTable({ title, rows, keyField }) {
    return (
      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-border text-xs font-black uppercase tracking-widest text-muted-foreground">{title}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border bg-background/40">
                {[keyField,"Crew","Valid","Expiring","Overdue","Compliance Bar"].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const pct = Math.round((row.valid / Math.max(row.valid + row.expiring + row.overdue, 1)) * 100);
                return (
                  <tr key={i} className="border-b border-border/40 hover:bg-secondary/20">
                    <td className="px-3 py-3 font-bold text-foreground">{row[Object.keys(row)[0]]}</td>
                    <td className="px-3 py-3 text-muted-foreground font-mono">{row.crew_count}</td>
                    <td className="px-3 py-3 text-green-400 font-mono font-bold">{row.valid}</td>
                    <td className="px-3 py-3 text-amber-400 font-mono font-bold">{row.expiring}</td>
                    <td className="px-3 py-3 text-red-400 font-mono font-bold">{row.overdue}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <ComplianceBar valid={row.valid} expiring={row.expiring} overdue={row.overdue} />
                        <span className="font-mono text-xs font-bold text-muted-foreground">{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        {[["#22c55e","Valid"],["#f59e0b","Expiring"],["#ef4444","Overdue"]].map(([c,l]) => (
          <div key={l} className="flex items-center gap-1.5">
            <div className="w-3 h-1.5 rounded-sm" style={{ background: c }} />
            <span>{l}</span>
          </div>
        ))}
      </div>
      <SectionTable title="By Base" rows={by_base} keyField="Base" />
      <SectionTable title="By Rank / Position" rows={by_rank} keyField="Rank" />
    </div>
  );
}

// ─── Tab: Schedule ────────────────────────────────────────────────────────────
function ScheduleTab({ schedule }) {
  const grouped = { overdue: [], this_month: [], next_quarter: [] };
  for (const s of schedule) {
    (grouped[s.urgency] || grouped.next_quarter).push(s);
  }
  const URGENCY_LABELS = {
    overdue:      { label: "OVERDUE — Immediate Action", color: "text-red-400",    border: "border-red-500/30",    bg: "bg-red-500/5"    },
    this_month:   { label: "This Month",                 color: "text-orange-400", border: "border-orange-500/30", bg: "bg-orange-500/5" },
    next_quarter: { label: "Next 3 Months",              color: "text-amber-400",  border: "border-amber-500/30",  bg: "bg-amber-500/5"  },
  };

  return (
    <div className="space-y-5">
      {Object.entries(grouped).map(([key, items]) => {
        if (!items.length) return null;
        const cfg = URGENCY_LABELS[key];
        return (
          <div key={key} className={`border rounded-sm overflow-hidden ${cfg.border} ${cfg.bg}`}>
            <div className={`px-4 py-2.5 border-b flex items-center gap-2 ${cfg.border}`}>
              <span className={`text-xs font-black uppercase tracking-widest ${cfg.color}`}>{cfg.label}</span>
              <span className="text-xs text-muted-foreground">({items.length} records)</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border/40">
                    {["Employee","Name","Rank","Base","Module","Expiry","Days","Reg. Ref"].map(h => (
                      <th key={h} className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.slice(0, 50).map((s, i) => (
                    <tr key={i} className="border-b border-border/30 hover:bg-secondary/20">
                      <td className="px-3 py-2 font-mono text-[11px] text-blue-400">{s.employee_id}</td>
                      <td className="px-3 py-2 font-medium text-foreground whitespace-nowrap">{s.crew_name}</td>
                      <td className="px-3 py-2 text-muted-foreground">{s.rank}</td>
                      <td className="px-3 py-2 font-mono text-muted-foreground">{s.base}</td>
                      <td className="px-3 py-2 text-foreground">{s.module_name}</td>
                      <td className="px-3 py-2 font-mono text-muted-foreground whitespace-nowrap">{s.expiry_date?.substring(0,10)}</td>
                      <td className={`px-3 py-2 font-mono font-bold whitespace-nowrap ${s.days_remaining < 0 ? "text-red-400" : s.days_remaining <= 30 ? "text-orange-400" : "text-amber-400"}`}>
                        {s.days_remaining < 0 ? `${Math.abs(s.days_remaining)}d OD` : `${s.days_remaining}d`}
                      </td>
                      <td className="px-3 py-2 text-[11px] font-mono text-muted-foreground">{s.regulatory_ref}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Export Dropdown ─────────────────────────────────────────────────────────
function ExportMenu({ tab, overview, expiring, matrix, schedule }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  function exportExpiryCSV() {
    exportCSV({
      headers: [
        { label: "Employee ID", key: "employee_id" }, { label: "Name", key: "crew_name" },
        { label: "Rank", key: "rank" }, { label: "Base", key: "base" },
        { label: "Module", key: "module_name" }, { label: "Expiry Date", key: "expiry_date" },
        { label: "Days Remaining", key: "days_remaining" }, { label: "Status", key: "status" },
        { label: "Regulatory Ref", key: "regulatory_ref" },
      ],
      rows: expiring,
      filename: `training-expiry-watch-${todayStr()}.csv`,
    });
    setOpen(false);
  }

  function exportExpiryPDF() {
    exportPDF({
      title: "Training Expiry Watch Report",
      subtitle: `${expiring.length} records · DGCA recurrency tracking · ${todayStr()}`,
      columns: [
        { header: "Emp ID",       key: "employee_id",    width: 18 },
        { header: "Name",         key: "crew_name",      width: 36 },
        { header: "Rank",         key: "rank",           width: 18 },
        { header: "Base",         key: "base",           width: 12 },
        { header: "Module",       key: "module_name",    width: 38 },
        { header: "Expiry",       key: "expiry_date",    width: 22 },
        { header: "Days Left",    key: "days_remaining", width: 16 },
        { header: "Status",       key: "status",         width: 20 },
        { header: "Reg. Ref",     key: "regulatory_ref", width: 24 },
      ],
      rows: expiring.map(r => ({ ...r, expiry_date: r.expiry_date?.substring(0, 10), status: r.status?.toUpperCase() })),
      filename: `training-expiry-watch-${todayStr()}.pdf`,
    });
    setOpen(false);
  }

  function exportOverviewPDF() {
    if (!overview) return;
    exportPDF({
      title: "Training Compliance Dashboard",
      subtitle: `${overview.crew_count} crew · ${overview.total_records} records · Compliance ${overview.compliance_pct}%`,
      columns: [
        { header: "Module",       key: "label",   width: 48 },
        { header: "Reg. Ref",     key: "code",    width: 30 },
        { header: "Valid",        key: "valid",   width: 16 },
        { header: "Expiring",     key: "expiring",width: 16 },
        { header: "Overdue",      key: "overdue", width: 16 },
        { header: "Total",        key: "total",   width: 14 },
      ],
      rows: overview.modules || [],
      filename: `training-compliance-${todayStr()}.pdf`,
    });
    setOpen(false);
  }

  function exportScheduleCSV() {
    exportCSV({
      headers: [
        { label: "Employee ID", key: "employee_id" }, { label: "Name", key: "crew_name" },
        { label: "Rank", key: "rank" }, { label: "Base", key: "base" },
        { label: "Module", key: "module_name" }, { label: "Expiry Date", key: "expiry_date" },
        { label: "Days Remaining", key: "days_remaining" }, { label: "Urgency", key: "urgency" },
        { label: "Regulatory Ref", key: "regulatory_ref" },
      ],
      rows: schedule,
      filename: `training-schedule-${todayStr()}.csv`,
    });
    setOpen(false);
  }

  const actions = [
    { label: "Expiry Watch — PDF",       icon: FileText,        fn: exportExpiryPDF },
    { label: "Expiry Watch — CSV",        icon: FileSpreadsheet, fn: exportExpiryCSV },
    { label: "Compliance Dashboard — PDF",icon: FileText,        fn: exportOverviewPDF },
    { label: "Schedule — CSV",            icon: FileSpreadsheet, fn: exportScheduleCSV },
  ];

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 px-3 py-2 border border-border text-xs text-muted-foreground hover:text-foreground hover:border-primary/40 rounded-sm transition-colors"
        data-testid="training-export-btn">
        <Download size={12} /> Export <ChevronDown size={10} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-sm shadow-lg z-50 py-1 min-w-[220px]">
          <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground border-b border-border mb-1">Download Report</div>
          {actions.map(({ label, icon: Icon, fn }) => (
            <button key={label} onClick={fn}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-primary/10 transition-colors text-left">
              <Icon size={11} className="text-muted-foreground shrink-0" /> {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
const TABS = [
  { id: "overview",  label: "Dashboard",       Icon: TrendingUp  },
  { id: "expiry",    label: "Expiry Watch",     Icon: AlertTriangle },
  { id: "matrix",    label: "Compliance Matrix",Icon: Shield      },
  { id: "schedule",  label: "Schedule",         Icon: Clock       },
];

export default function TrainingEngine() {
  const [tab,      setTab]      = useState("overview");
  const [overview, setOverview] = useState(null);
  const [expiring, setExpiring] = useState([]);
  const [matrix,   setMatrix]   = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [wizard,   setWizard]   = useState(null); // null | preselected[]

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, ex, mx, sc] = await Promise.allSettled([
        trainingApi.getOverview(),
        trainingApi.getExpiring(90),
        trainingApi.getComplianceMatrix(),
        trainingApi.getSchedule(),
      ]);
      if (ov.status === "fulfilled") setOverview(ov.value.data);
      if (ex.status === "fulfilled") setExpiring(ex.value.data || []);
      if (mx.status === "fulfilled") setMatrix(mx.value.data);
      if (sc.status === "fulfilled") setSchedule(sc.value.data || []);
    } catch (e) { console.error("Training load error:", e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 space-y-5" data-testid="training-engine">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-foreground tracking-tight">Training Engine</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            DGCA recurrency tracking · {overview?.crew_count ?? "—"} crew · {overview?.total_records ?? "—"} training records
          </p>
        </div>
        <div className="flex items-center gap-3">
          {overview?.overdue > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-sm" data-testid="training-overdue-alert">
              <AlertTriangle size={13} className="text-red-400" />
              <span className="text-xs font-bold text-red-400">{overview.overdue} overdue records</span>
            </div>
          )}
          <button
            onClick={() => setWizard([])}
            className="flex items-center gap-1.5 px-3 py-2 bg-primary text-primary-foreground text-xs font-bold rounded-sm hover:bg-primary/90 transition-colors"
            data-testid="training-schedule-btn">
            <Clock size={12} /> Schedule Training
          </button>
          <ExportMenu tab={tab} overview={overview} expiring={expiring} matrix={matrix} schedule={schedule} />
          <button onClick={load} disabled={loading}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            data-testid="training-refresh">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {TABS.map(({ id, label, Icon }) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`training-tab-${id}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold transition-colors border-b-2 -mb-px ${
              tab === id
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}>
            <Icon size={12} />{label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading && !overview ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <RefreshCw size={18} className="animate-spin mr-2" />
          <span className="text-sm">Loading training records...</span>
        </div>
      ) : (
        <>
          {tab === "overview"  && <OverviewTab      overview={overview} />}
          {tab === "expiry"    && <ExpiryWatchTab   records={expiring} onScheduleSelected={setWizard} />}
          {tab === "matrix"    && <ComplianceMatrixTab matrix={matrix} />}
          {tab === "schedule"  && <ScheduleTab      schedule={schedule} />}
        </>
      )}

      {/* Training Scheduling Wizard */}
      {wizard !== null && (
        <TrainingWizardModal
          preselected={wizard}
          onClose={() => setWizard(null)}
          onScheduled={() => { setWizard(null); load(); }}
        />
      )}
    </div>
  );
}
